import { Badge, Box, Button, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Radio, RadioGroup, Stack, Text, useDisclosure, useToast } from '@chakra-ui/react';
import DashLayout from '../../components/DashLayout';
import { PageHeader } from '../../components/PageHeader';
import { Loading } from '../../components/Loading';
import { FaPlus } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import {  HomeworkGet, buscarBookPorId } from '../../services/api';
import { BookGet } from '../Books';
import { DrawerCreate } from './DrawerCreate';
import { DrawerUpdate } from './DrawerUpdate';
import ButtonOptions from '../../components/ButtonOptions';
import useAuth from '../../hooks/useAuth';
import { BsFileEarmarkMedicalFill } from 'react-icons/bs';
import { abrirMaterialDoBook } from '../../utils/materialWindow';

export type Lesson = {
	id: string;
	nome: string;
	canvaUrl: string;
	bookId: string;
	thumbnail:string;
	homeworks: HomeworkGet[];
};

const LessonDefault = {
	id: '',
	nome: '',
	canvaUrl: '',
	bookId: '',
	thumbnail:'',
	homeworks:[]
};

const SEM_FILTRO = 'Todos';

// Os nomes vem do banco com acento, caixa e espacos inconsistentes ("LIÇÃO 7", "Lección 2 "),
// entao qualquer comparacao ignora acento, caixa e espaco nas pontas.
const semAcento = (texto: string) => texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

// O filtro olha o nome da lesson, nao o idioma do book. Amarrar o termo ao idioma quebrava
// sempre que o cadastro divergia da traducao esperada: o book de Espanhol tem "Revisión 1"
// enquanto a tabela procurava por "Revisar", e o filtro de reviews vinha vazio.
// As palavras cobrem os idiomas cadastrados, e tudo que nao for review conta como lesson,
// entao nomes fora do padrao ("CHALLENGE 1") e typos ("Leccíon 7") continuam aparecendo
// em vez de sumir dos dois filtros.
const PalavrasDeReview = [ 'review', 'revisao', 'revision', 'revisione', 'revisar', 'repaso', 'examen' ];

const ehReview = (nome: string) => {
	const normalizado = semAcento(nome);
	return PalavrasDeReview.some((palavra) => normalizado.startsWith(palavra));
};

export const Lessons = () => {
	const { bookId } = useParams();
	const toast = useToast();

	const [ lessons, setLessons ] = useState<Lesson[]>([]);
	const [ homeworks, setHomeworks ] = useState<HomeworkGet[]>([]);
	const [ book, setBook ] = useState<BookGet>();
	const [ loading, setLoading ] = useState<boolean>(true);
	const [lessonSelected, setLessonSelected] = useState<Lesson>(LessonDefault);

	const [ filter, setFilter ] = useState<string>(SEM_FILTRO);

	const {user} = useAuth();
	const createDisclosure = useDisclosure();
	const updateDisclosure = useDisclosure();
	const modalDisclosure = useDisclosure();


    function justNumbers(text:string) {
        var numbers = text.replace(/[^0-9]/g,'');
        return parseInt(numbers);
    }
	
	const buscarLessons = () => {
			buscarBookPorId(`${bookId}`)
				.then((response) => {
					// A API pode responder 200 com corpo vazio (book inexistente).
					const lessonsData: Lesson[] = response.data?.lessons ?? [];
					lessonsData.sort();
					setBook(response.data || undefined);
					setLessons(lessonsData);
					setLoading(false);
				})
				.catch((err) => {
					toast({
						title: `Não foi possível carregar o livro${err}`,
						status: 'error',
						isClosable: true
					});
				});
		}

	useEffect(
		() => {
			buscarLessons();
			// Carrega uma vez ao montar. `buscarLessons` e recriada a cada render, entao
			// incluir a dependencia faria o efeito buscar em loop infinito.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},[ ]
	);

	// A lista exibida e derivada de `lessons` + `filter` em vez de viver num state
	// paralelo: com o state duplicado, cada refetch (o `afterClose` de criar/editar)
	// reescrevia a lista completa por cima da filtrada e a tela piscava sem filtro
	// ate o efeito reaplica-lo.
	const lessonsViewd = useMemo(() => {
		if(filter === SEM_FILTRO) return lessons;

		const querReview = filter === "Review";
		return lessons.filter((l)=> ehReview(l.nome) === querReview);
	}, [ lessons, filter ]);

	return (
		<DashLayout>
			<Flex width="100%" height="100%" flexDir="column">
				<PageHeader
					title={
						<Flex as="span" display="inline-flex" alignItems="center" gap="10px" flexWrap="wrap">
							Lessons / {book?.nome}
							<Badge>{book?.idioma}</Badge>
							<Badge colorScheme="blue">{book?.nivel}</Badge>
						</Flex>
					}
				>
					{
						user?.userType === "ADMIN" &&
						<Flex justifyContent="flex-end">
							<Button leftIcon={<FaPlus />} colorScheme="blue" variant="solid" onClick={createDisclosure.onOpen}>
								Novo
							</Button>
						</Flex>
					}
					<Flex 
						gap="10px" 
						borderRadius="10px" 
						justifyContent="center" 
						alignItems="center" 
						p="5px"
						mt="10px"
					>
                       <RadioGroup onChange={(e)=>setFilter(e)} 
							value={filter}
					   >
						<Stack direction='row'>
							<Radio value={SEM_FILTRO}>Todos</Radio>
							<Radio value='Lesson'>Lessons</Radio>
							<Radio value='Review'>Reviews</Radio>
						</Stack>
						</RadioGroup>
                    </Flex>
				</PageHeader>
				<Flex
					height="100%"
					gap="20px"
					justifyContent="center"
					padding="50px"
					flexWrap="wrap"
					overflowY="scroll"
				>
					{!loading ? (
						[...lessonsViewd]
							.sort((a, b) => {
								if (Number(a.nome.split(' ')[1]) < Number(b.nome.split(' ')[1])) return -1;
								if (Number(a.nome.split(' ')[1]) > Number(b.nome.split(' ')[1])) return 1;
								return 0;
							})
							.map((lesson, index) => {
								return (
										<Flex
											width="280px"
											height="100px"
											bgColor="blue.700"
											borderRadius="15px"
											alignItems="center"
											padding="10px"
											gap="10px"
											key={index}
											boxShadow="lg"
										>
											<Flex
												height="80px"
												bgImage={
													lesson.thumbnail
												}
												minWidth="80px"
												maxWidth="80px"
												bgPosition="center"
												bgSize="cover"
												borderRadius="15px"
											/>
											<Flex flexDir="column">
													<Flex alignItems="center" gap="10px">
														<Text 
															color="white" 
															fontSize="20px" 
															fontWeight="800" 
															textAlign="center"
														>
															{lesson.nome}
														</Text>
														<ButtonOptions
														isBook={false}
															onView={() => {
																abrirMaterialDoBook(lesson.bookId, lesson.id)
															}}
															onEdit={()=>{
																const lessonEdited = lessons.find((l) => l.id === lesson.id)
																if(lessonEdited){
																	setLessonSelected(lessonEdited);
																	updateDisclosure.onOpen();
																}
															}}
															onOpen={() => {
																abrirMaterialDoBook(lesson.bookId, lesson.id)
															}}
															openHomeworks={()=>{
																const filtered = lessons.find(l => l.id === lesson.id)?.homeworks;
																if(filtered){
																	setHomeworks(filtered);
																	modalDisclosure.onOpen();
																}
															}}
														/>
													</Flex>
												</Flex>
										</Flex>
									)
								}
							)
					) : (
						<Loading />
					)}
				</Flex>
			</Flex>
			<DrawerCreate
				isOpen={createDisclosure.isOpen}
				onClose={createDisclosure.onClose}
				onOpen={createDisclosure.onOpen}
				afterClose={() =>{
					buscarLessons();
					createDisclosure.onClose();
				}}
				bookId={`${bookId}`}
			/>
			<DrawerUpdate
				isOpen={updateDisclosure.isOpen}
				onClose={updateDisclosure.onClose}
				onOpen={updateDisclosure.onOpen}
				afterClose={buscarLessons}
				bookId={`${bookId}`}
				lesson={lessonSelected}
			/>

			<Modal isOpen={modalDisclosure.isOpen} onClose={modalDisclosure.onClose}>
				<ModalOverlay />
				<ModalContent>
				<ModalHeader>Homeworks</ModalHeader>
				<ModalCloseButton />
				<ModalBody padding="20px">
				{
                        
					homeworks
						.sort()
						.sort((a, b) => Number(justNumbers(b.nome)) - Number(justNumbers(a.nome)))
						.map((home)=>
							<Box
								key={home.id}
								width="100%"
								height="50px"
								borderWidth="1px"
								borderColor="gray.200"
								borderRadius="10px"
								boxShadow="sm"
								bgColor="white"
								cursor="pointer"
								mb="20px"
								onClick={()=>{
									window.open(home.materialUrl,'_blank');
								}}
							>
								<Flex
									height="100%"
									justifyContent="space-around"
									alignItems="center"
								>
									<Flex 
										justifyContent="center"
										alignItems="center"
										gap="10px"
									>
										<BsFileEarmarkMedicalFill size="20px"/>
										<Text fontSize="18px" fontWeight="semibold" >
											{home.nome}
										</Text>
									</Flex>
									
								</Flex>
							</Box>
						)
					}
				</ModalBody>
				
				</ModalContent>
			</Modal>

		</DashLayout>
	);
};
